// context/OrderContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react'
import { createOrder as apiCreateOrder } from '../services/services'
import { haversine_distance } from '../utils/distance'
import { UserContext } from './userContext'
import { useStoreSettings } from '../hooks/useStoreSettings'

const OrderContext = createContext()
const ORIGIN = { lat: -33.4376397, lng: -70.6158242 } // punto base del local

export const OrderProvider = ({ children }) => {
  const { userAddress } = useContext(UserContext)
  const {
    fixed,
    variable,
    deliveryRadius,
    isDeliveryAvailable,
  } = useStoreSettings()

  const [order, setOrder] = useState({
    Order_Items: [],
    status: 'initialized',
  })

  const [deliveryType, setDeliveryType] = useState('Retiro en Tienda')
  const [hasRestored, setHasRestored] = useState(false)

  // 🧠 Cálculos derivados
  const { shippingCost, subtotal, discountValue, totalAmount } = useMemo(() => {
    const distance = userAddress ? haversine_distance(userAddress, ORIGIN) : 0

    const validDelivery =
      deliveryType === 'Despacho a Domicilio' &&
      isDeliveryAvailable &&
      distance <= deliveryRadius

    const shippingCost = validDelivery
      ? fixed + variable * Math.floor(distance)
      : 0

    const subtotal =
      order.Order_Items?.reduce(
        (sum, item) =>
          sum +
          (item.Variant.price +
            item.Addition_Items.reduce((a, b) => a + b.price, 0)) *
          item.quantity,
        0
      ) || 0

    const discountValue = order.discount
      ? order.discount.discountAmount != null
        ? order.discount.discountAmount
        : Math.round(
          subtotal * (order.discount.discountPercentage || 0) / 100
        )
      : 0

    const totalAmount = subtotal - discountValue + shippingCost

    return { shippingCost, subtotal, discountValue, totalAmount }
  }, [
    order.Order_Items,
    order.discount,
    userAddress,
    deliveryType,
    fixed,
    variable,
    deliveryRadius,
    isDeliveryAvailable,
  ])

  // 🔁 Restaurar carrito y guestId una sola vez
  useEffect(() => {
    const stored = localStorage.getItem('order')
    if (stored) {
      try {
        const { data, timestamp } = JSON.parse(stored)
        if (Date.now() - timestamp < 43_200_000) {
          setOrder(data)
        } else {
          localStorage.removeItem('order')
        }
      } catch (err) {
        console.error('Error parsing or expiring order from localStorage', err)
        localStorage.removeItem('order')
      }
    }

    if (!localStorage.getItem('guestId')) {
      localStorage.setItem('guestId', crypto.randomUUID())
    }

    setHasRestored(true)
  }, [])

  // 💾 Guardar o limpiar orden en localStorage
  useEffect(() => {
    if (!hasRestored || !order) return

    if (order.status === 'completed') {
      localStorage.removeItem('order')
    } else {
      localStorage.setItem(
        'order',
        JSON.stringify({
          data: order,
          timestamp: Date.now(),
        })
      )
    }
  }, [order, hasRestored])

  // 🔄 Actualizar status según productos, solo si ya cargamos el storage
  useEffect(() => {
    if (!hasRestored) return

    setOrder(prev => {
      if (prev.status === 'created' || prev.status === 'completed') return prev
      return {
        ...prev,
        status:
          prev.Order_Items && prev.Order_Items.length > 0
            ? 'initialized'
            : 'orderWithoutProducts',
      }
    })
  }, [order.Order_Items, hasRestored])

  // 🚀 Crear orden en backend si está lista
  useEffect(() => {
    if (!hasRestored) return

    if (!order.idSellOrder && order.status === 'initialized') {
      const create = async () => {
        try {
          const created = await apiCreateOrder({
            shippingCost,
            totalAmount,
            orderItems: order.Order_Items,
            guestId: localStorage.getItem('guestId'),
          })
          setOrder(created)
        } catch (err) {
          console.error('Error creating order:', err)
        }
      }
      create()
    }
  }, [order.status, order.idSellOrder, totalAmount, shippingCost, hasRestored, order.Order_Items])

  return (
    <OrderContext.Provider
      value={{
        order,
        setOrder,
        deliveryType,
        setDeliveryType,
        shippingCost,
        subtotal,
        discountValue,
        totalAmount,
      }}
    >
      {children}
    </OrderContext.Provider>
  )
}

export const useOrder = () => useContext(OrderContext)
